import PinwheelModal, {
  ErrorEventPayload,
  EventHandler,
  EventName,
  EventPayloadMap,
} from '@pinwheel/react-modal';
import { Mutation } from '@varo/graphql-schema-types';
import { useContext, useEffect, useRef } from 'react';
import { View } from 'react-native';
import PinWheelLinkEvents from 'services/graphql/mutations/PinWheelLinkEvents';
import NetworkStatus from 'shared/components/NetworkStatus/NetworkStatus';
import { PinwheelEvents } from 'shared/constants/pinWheel';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { useMutation } from 'urql';

import {
  categoryForSolutionType,
  PinwheelSolutionType,
} from './helpers/pinwheelSolutionType.helpers';
import {
  PinwheelInputAllocationPayload,
  PinWheelModalWrapperProps,
  pinwheelSegmentEventNames,
} from './PinwheelModalWrapper.helpers';
import usePinwheelEventsHandling from './usePinwheelEventsHandling/usePinwheelEventsHandling';

const isPinwheelInputAllocationPayload = (
  payload: EventPayloadMap,
): payload is PinwheelInputAllocationPayload & EventPayloadMap => {
  return (
    'allocation' in payload &&
    typeof payload.allocation === 'object' &&
    payload.allocation !== null
  );
};

const PinWheelModalWrapper = ({
  token,
  solutionType = PinwheelSolutionType.DirectDeposit,
  sourceName,
  linkTokenId,
  onOpen,
  onAbruptExit,
  segmentClient,
}: PinWheelModalWrapperProps): React.ReactElement => {
  const successRef = useRef(false);
  const amountRef = useRef(0);
  const isPinwheelLoginSuccessRef = useRef(false);
  const allocationTypeRef = useRef<string | null>(null);
  const allocationValueRef = useRef<number | null>(null);
  const hasPinwheelErrorEventRef = useRef(false);

  const usesEventsHandlingHook =
    solutionType === PinwheelSolutionType.BillSwitch ||
    solutionType === PinwheelSolutionType.BillManager;

  const resolvedSourceName = sourceName;
  const resolvedCategory = categoryForSolutionType(solutionType);

  const { varoSessionId: isSessionActive } =
    useContext<AuthContextProps>(AuthContext);
  const previousIsSessionActiveRef = useRef(Boolean(isSessionActive));
  const hasTriggeredExitRef = useRef(false);

  useEffect(() => {
    return () => {
      successRef.current = false;
      amountRef.current = 0;
      isPinwheelLoginSuccessRef.current = false;
      allocationTypeRef.current = null;
      allocationValueRef.current = null;
      hasPinwheelErrorEventRef.current = false;
    };
  }, []);

  useEffect(() => {
    hasTriggeredExitRef.current = false;
  }, [token]);

  useEffect(() => {
    const wasActive = previousIsSessionActiveRef.current;
    const isActive = Boolean(isSessionActive);

    if (wasActive && !isActive && !hasTriggeredExitRef.current) {
      hasTriggeredExitRef.current = true;
      //to avoid double trigger on onAbruptExit as it will be triggered when modal closes
      onAbruptExit({
        isEarlyExit: true,
        hasPinwheelErrorEvent: hasPinwheelErrorEventRef.current,
        hasLoginSuccess: isPinwheelLoginSuccessRef.current,
      });
    }

    previousIsSessionActiveRef.current = isActive;
  }, [isSessionActive, onAbruptExit]);

  const { onEvent: eventsHandlingOnEvent, onExit: eventsHandlingOnExit } =
    usePinwheelEventsHandling({
      onOpen,
      onOutcome: (outcome) => {
        onAbruptExit({
          // in the future this can be updated to be a flag
          // on the state, but the rules for setting this value are
          // not clear to me.
          isEarlyExit: false,
          hasPinwheelErrorEvent: Boolean(outcome.pinwheelError),
          hasLoginSuccess: isPinwheelLoginSuccessRef.current,
          sessionState: outcome,
        });
      },
      linkTokenId,
      category: categoryForSolutionType(solutionType),
      sourceName: sourceName,
      segmentClient,
    });

  const {
    Open,
    SelectEmployer,
    SelectPlatform,
    LoginAttempt,
    Login,
    InputAmount,
    InputAllocation,
    Success,
    PinWheelError,
  } = PinwheelEvents;

  const [, executeMutation] = useMutation<Mutation>(PinWheelLinkEvents);
  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU,
  ]);
  const logPinwheelEvent = (
    event: string,
    eventProperties?: Record<string, unknown>,
  ) => {
    void segmentClient.logEvent(event, {
      event,
      ...eventProperties,
    });
  };

  const logPinwheelScreenEvent = (
    screenName: string,
    eventProperties?: Record<string, unknown>,
  ) => {
    void segmentClient.logScreenEvent(screenName, {
      ...eventProperties,
    });
  };

  const onEvent = (eventName: EventName, eventPayload: EventPayloadMap) => {
    if (eventName === Open) {
      onOpen();
    }
    void executeMutation({
      eventName,
      linkTokenId,
      eventPayload: JSON.stringify(eventPayload),
    });
    const segmentEventPayload: Record<string, unknown> = {
      ...eventPayload,
    };

    let segmentEventName: string = '';
    let eventProperties: Record<string, unknown> = {};

    const {
      selectedEmployerId,
      selectedEmployerName,
      selectedPlatformId,
      selectedPlatformName,
      platformId,
      accountId,
      value,
      type,
      code,
      message,
      pendingRetry,
      screenName,
    } = segmentEventPayload;
    switch (eventName) {
      case SelectEmployer:
        segmentEventName =
          pinwheelSegmentEventNames[PinwheelEvents.SelectEmployer];
        eventProperties = {
          source_name: resolvedSourceName,
          category: resolvedCategory,
          payroll_employer_id: selectedEmployerId,
          payroll_employer_name: selectedEmployerName,
        };
        break;
      case SelectPlatform:
        segmentEventName =
          pinwheelSegmentEventNames[PinwheelEvents.SelectPlatform];
        eventProperties = {
          source_name: resolvedSourceName,
          category: resolvedCategory,
          payroll_platform_id: selectedPlatformId,
          payroll_platform_name: selectedPlatformName,
        };
        break;
      case LoginAttempt:
        segmentEventName =
          pinwheelSegmentEventNames[PinwheelEvents.LoginAttempt];
        eventProperties = {
          source_name: resolvedSourceName,
          category: resolvedCategory,
          payroll_platform_id: platformId,
        };
        break;
      case Login: {
        isPinwheelLoginSuccessRef.current = true;
        segmentEventName = pinwheelSegmentEventNames[PinwheelEvents.Login];
        eventProperties = {
          source_name: resolvedSourceName,
          category: resolvedCategory,
          payroll_account_id: accountId,
          payroll_platform_id: platformId,
        };
        break;
      }
      case InputAmount: {
        segmentEventName =
          pinwheelSegmentEventNames[PinwheelEvents.InputAmount];
        amountRef.current = (value as number) * 100;
        eventProperties = {
          amount: amountRef.current,
        };
        break;
      }
      case InputAllocation: {
        segmentEventName =
          pinwheelSegmentEventNames[PinwheelEvents.InputAllocation];

        if (isPinwheelInputAllocationPayload(eventPayload)) {
          const allocationPayload = eventPayload;
          const allocation = allocationPayload.allocation;

          allocationTypeRef.current = allocation?.type ?? null;
          allocationValueRef.current = allocation?.value ?? null;
        } else {
          allocationTypeRef.current = 'percentage';
          allocationValueRef.current = 100;
        }

        eventProperties = {
          source_name: resolvedSourceName,
          category: resolvedCategory,
          input_type:
            allocationTypeRef.current === 'amount'
              ? 'fixed_amount'
              : allocationTypeRef.current,
          dd_switch_type: allocationTypeRef.current,
          dd_amount:
            allocationTypeRef.current === 'amount'
              ? allocationValueRef.current
              : undefined,
          dd_percentage:
            allocationTypeRef.current === 'percentage'
              ? allocationValueRef.current
              : undefined,
        };
        break;
      }
      case Success: {
        successRef.current = true;
        segmentEventName = pinwheelSegmentEventNames[PinwheelEvents.Success];
        eventProperties = {
          source_name: resolvedSourceName,
          category: resolvedCategory,
          outcome: 'success',
          payroll_account_id: accountId,
          payroll_platform_id: platformId,
          dd_switch_type: allocationTypeRef.current,
          dd_amount:
            allocationTypeRef.current === 'amount'
              ? allocationValueRef.current
              : undefined,
          dd_percentage:
            allocationTypeRef.current === 'percentage'
              ? allocationValueRef.current
              : undefined,
        };
        refreshTiles(); // Refresh hometiles for you section
        break;
      }
      case PinWheelError: {
        hasPinwheelErrorEventRef.current = true;
        segmentEventName =
          pinwheelSegmentEventNames[PinwheelEvents.PinWheelError];
        eventProperties = {
          source_name: resolvedSourceName,
          category: resolvedCategory,
          type,
          code,
          message,
          pending_retry: pendingRetry,
        };
        break;
      }
      default:
        if (eventName.toString() === 'screen_transition') {
          const pinwheelScreenName = (screenName as string).toLowerCase();
          const segmentEventName =
            pinwheelSegmentEventNames[pinwheelScreenName];
          const isLoginScreen = pinwheelScreenName === 'login';

          if (
            isLoginScreen &&
            typeof selectedPlatformName === 'string' &&
            selectedPlatformName.includes('Partnership')
          ) {
            const partnershipEventName = 'PreMatch Platform Selected';
            const partnershipPayload = {
              name: partnershipEventName,
              category: resolvedCategory,
              payroll_platform_id: selectedPlatformId,
              payroll_platform_name: selectedPlatformName,
            };

            logPinwheelEvent(partnershipEventName, partnershipPayload);
          }

          const baseEventName = isLoginScreen
            ? 'Direct Deposit Instant Switch Login'
            : segmentEventName;

          const baseEventPayload = isLoginScreen
            ? {
                category: resolvedCategory,
                name: baseEventName,
                payroll_employer_id: selectedEmployerId,
                payroll_employer_name: selectedEmployerName,
                payroll_platform_id: selectedPlatformId,
                payroll_platform_name: selectedPlatformName,
              }
            : {
                category: resolvedCategory,
                name: baseEventName,
              };

          logPinwheelScreenEvent(baseEventName, baseEventPayload);
          // screen_transition is fully handled above — do not fall through to the dd_form chain
          return;
        }

        if (eventName.toString() === 'dd_form_begin') {
          segmentEventName =
            pinwheelSegmentEventNames[PinwheelEvents.DdFormBegin];
          eventProperties = {
            category: resolvedCategory,
          };
        } else if (eventName.toString() === 'dd_form_create') {
          segmentEventName =
            pinwheelSegmentEventNames[PinwheelEvents.DdFormCreate];
          eventProperties = {
            category: resolvedCategory,
          };
        } else if (eventName.toString() === 'dd_form_download') {
          segmentEventName =
            pinwheelSegmentEventNames[PinwheelEvents.DdFormDownload];
          eventProperties = {
            category: resolvedCategory,
          };
        } else {
          logErrorWithContext(
            new Error(`Unknown Pinwheel event received: ${eventName}`),
            {
              featureArea: FeatureArea.PAYMENTS,
              responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
              severity: ErrorSeverity.LOW,
              additionalContext: { eventName, eventPayload },
            },
          );
          return;
        }
    }

    if (segmentEventName) {
      logPinwheelEvent(segmentEventName, eventProperties);
    }
  };

  const onExit = (error?: ErrorEventPayload) => {
    if (!isSessionActive && hasTriggeredExitRef.current) {
      return;
    }

    if (error && isPinwheelLoginSuccessRef.current) {
      const { type, code, message, pendingRetry } = error;
      onAbruptExit({
        isEarlyExit: true,
        hasPinwheelErrorEvent: hasPinwheelErrorEventRef.current,
        hasLoginSuccess: isPinwheelLoginSuccessRef.current,
      });
      logPinwheelEvent(pinwheelSegmentEventNames[PinwheelEvents.Success], {
        outcome: pendingRetry ? 'pending' : 'error',
        error_type: type,
        error_code: code,
        error_message: message,
        error_pending_retry: pendingRetry,
      });
    } else if (
      (!error && !successRef.current) ||
      !isPinwheelLoginSuccessRef.current
    ) {
      logPinwheelEvent(pinwheelSegmentEventNames.abandoned, {
        source_name: resolvedSourceName,
        category: resolvedCategory,
      });
      onAbruptExit({
        isEarlyExit: true,
        hasPinwheelErrorEvent: hasPinwheelErrorEventRef.current,
        hasLoginSuccess: isPinwheelLoginSuccessRef.current,
      });
    } else if (successRef.current) {
      onAbruptExit({
        isEarlyExit: !successRef.current,
        hasLoginSuccess: isPinwheelLoginSuccessRef.current,
      });
    }
  };

  const onEventValue = usesEventsHandlingHook ? eventsHandlingOnEvent : onEvent;
  const onExitValue = usesEventsHandlingHook ? eventsHandlingOnExit : onExit;
  const shouldOpenModal =
    Boolean(isSessionActive) && !hasTriggeredExitRef.current;
  //modal will stay open only when there is an active session and doesnt re open with stale varoSessionId value
  return (
    <View testID="pinwheel">
      <PinwheelModal
        open={shouldOpenModal}
        linkToken={token}
        onEvent={onEventValue as EventHandler}
        onExit={onExitValue}
      />
      {shouldOpenModal ? <NetworkStatus /> : null}
    </View>
  );
};

export default PinWheelModalWrapper;
